import angular from 'angular';

import r2a from '@web/angularIntegration/r2a';
import BarChartComponentReact from '@web/reports/shiftFeedback/charts/BarChartComponentAngular';
import LineGraphComponentReact from '@web/reports/shiftFeedback/charts/LineGraphComponentAngular';

import managePreviewRedirects from '../account/managePreviewRedirects';
import laborSpendingImage from '../assets/images/premium-locked-screen/reports/labor-spending.png';
import noShowImage from '../assets/images/premium-locked-screen/reports/noshow.png';
import ptoImage from '../assets/images/premium-locked-screen/reports/pto.png';
import { accessLevels, PREMIUM_FEATURE_NAMES } from '../constants';

import chartComponent from './components/chartComponent';
import reportsTableComponent from './components/reportsTableComponent';
import timeoffTableComponent from './components/timeoffTableComponent';
import userTimeoffTableComponent from './components/userTimeoffTableComponent';
import noShowTableComponent from './components/noShowTableComponent';
import salesTableComponent from './components/salesTableComponent';
import shiftFeedbackTableComponent from './components/shiftFeedbackTableComponent';
import reportsChartsComponent from './components/reportsChartsComponent';
import reportsSummaryComponent from './components/reportsSummaryComponent';
import configureTableColumnsComponent from './components/configureTableColumnsComponent';
import reportsSubNavComponent from './components/reportsSubNavComponent';
import payrollTableGroupedComponent from './components/payrollTableGroupedComponent';
import payrollTableUngroupedComponent from './components/payrollTableUngroupedComponent';
import reportDynamicFieldComponent from './components/reportDynamicFieldComponent';
import reportUserCellComponent from './components/reportUserCellComponent';
import reportTableColumn from './directives/reportTableColumn';
import salesTableColumn from './directives/salesTableColumn';
import ReportsServiceImport from './services/ReportsService';
import ReportsUtilsImport from './services/ReportsUtils';
import ReportsPrintPayloadReplacerImport from './services/ReportsPrintPayloadReplacer';
import indexUrl from './index.html';
import laborUrl from './labor.html';
import shiftFeedbackUrl from './shift-feedback.html';
import payrollUrl from './payroll.html';
import timeoffUrl from './timeoff.html';
import noShowUrl from './no-show.html';
import salesUrl from './sales.html';
import noAccessUrl from './no-access.html';
import previewUrl from './preview.html';

const reportsModule = angular.module('sling.reports', []);
reportsModule.config(($stateProvider) => {
  $stateProvider.state('reports', {
    templateUrl: indexUrl,
    url: '/reports',
    data: {
      title: 'Reports',
      access: accessLevels.manager,
    },
    resolve: {
      personaCollection(PersonaCollection) {
        return PersonaCollection.loadData();
      },
      laborUISchema(LaborUISchema) {
        return LaborUISchema.getSchema();
      },
      shiftUtils(ShiftUtils) {
        return ShiftUtils.loadAsync();
      },
    },
    onEnter(RateService) {
      RateService.triggerRateModal();
    },
  });

  $stateProvider.state('reports.labor', {
    templateUrl: laborUrl,
    controllerProvider: async () =>
      (await import(/* webpackChunkName: "reports" */ './LaborReportsCtrl')).default,
    controllerAs: 'ctrl',
    reloadOnSearch: false,
    url: '/labor?date',
    data: {
      title: 'Labor',
      access: accessLevels.manager,
    },
  });

  $stateProvider.state('reports.payroll', {
    templateUrl: payrollUrl,
    controllerProvider: async () =>
      (await import(/* webpackChunkName: "reports" */ './PayrollCtrl')).default,
    controllerAs: 'ctrl',
    reloadOnSearch: false,
    url: '/payroll?date',
    data: {
      title: 'Payroll',
      access: accessLevels.manager,
    },
  });

  $stateProvider.state('reports.shift-feedback', {
    templateUrl: shiftFeedbackUrl,
    controllerProvider: async () =>
      (await import(/* webpackChunkName: "reports" */ './ShiftFeedbackReportsCtrl')).default,
    controllerAs: 'ctrl',
    reloadOnSearch: false,
    url: '/shift-feedback?date',
    data: {
      title: 'Shift Feedback',
      access: accessLevels.manager,
    },
    resolve: {
      tagsService(TagsService) {
        return TagsService.loadData();
      },
      dayPartsService(DayPartsService) {
        return DayPartsService.loadData();
      },
    },
  });

  $stateProvider.state('reports.timeoff', {
    templateUrl: timeoffUrl,
    controllerProvider: async () =>
      (await import(/* webpackChunkName: "reports" */ './TimeoffReportsCtrl')).default,
    controllerAs: 'ctrl',
    reloadOnSearch: false,
    url: '/timeoff?date',
    data: {
      title: 'Time off',
      access: accessLevels.manager,
    },
  });

  $stateProvider.state('reports.no-show', {
    templateUrl: noShowUrl,
    controllerProvider: async () =>
      (await import(/* webpackChunkName: "reports" */ './NoShowReportsCtrl')).default,
    controllerAs: 'ctrl',
    reloadOnSearch: false,
    url: '/no-show?date',
    data: {
      title: 'No show',
      access: accessLevels.manager,
    },
  });

  $stateProvider.state('reports.sales', {
    templateUrl: salesUrl,
    controllerProvider: async () =>
      (await import(/* webpackChunkName: "reports" */ './SalesReportsCtrl')).default,
    controllerAs: 'ctrl',

    reloadOnSearch: false,
    url: '/sales?date',
    data: {
      title: 'Sales',
      access: accessLevels.manager,
    },
  });

  $stateProvider.state('reports.no-access', {
    url: '/no-access',
    templateUrl: noAccessUrl,
    data: {
      title: 'No access',
      access: accessLevels.manager,
      cacheSubstate: false,
    },
  });

  $stateProvider.state('reports.preview', {
    url: '/preview',
    templateUrl: previewUrl,
    data: {
      title: 'Preview',
      access: accessLevels.manager,
      cacheSubstate: false,
    },
    controllerAs: 'ctrl',
    controller: function controller() {
      this.laborSpendingImage = laborSpendingImage;
      this.ptoImage = ptoImage;
      this.noShowImage = noShowImage;
      this.REPORTS_FEATURE_NAME = PREMIUM_FEATURE_NAMES.reports;
    },
  });
});

const statesRequiringLaborCostAccess = ['reports.labor', 'reports.payroll', 'reports.sales'];

function getDefaultSubstate(Authentication, LaborUISchema) {
  let defaultState = 'reports.labor';

  if (LaborUISchema.schema && !LaborUISchema.schema.laborReportUISchema) {
    defaultState = 'reports.payroll';
  }

  let defaultSubstate = Authentication.user.clientcache.reports.lastUIState || defaultState;

  if (LaborUISchema.schema && !LaborUISchema.schema.laborReportUISchema) {
    defaultSubstate = 'reports.payroll';
  }

  return defaultSubstate;
}

reportsModule.run(($transitions, Authentication, LaborUISchema) => {
  $transitions.onStart(
    {
      to: 'reports.**',
    },
    async (transition) => {
      if (!Authentication.user.clientcache.reports) {
        Authentication.user.clientcache.reports = {};
      }

      await LaborUISchema.getSchema();

      const redirect = managePreviewRedirects(
        Authentication,
        transition,
        'reports',
        'reports',
        getDefaultSubstate(Authentication, LaborUISchema)
      );

      if (redirect) {
        return redirect;
      }

      if (transition.$to().name === 'reports.labor' && !LaborUISchema.schema.laborReportUISchema) {
        return transition.router.stateService.target('reports');
      }

      if (
        statesRequiringLaborCostAccess.includes(transition.$to().name) &&
        !Authentication.user.hasAccessToLaborCost
      ) {
        return transition.router.stateService.target('reports.no-access');
      }

      if (transition.$to().data?.cacheSubstate !== false) {
        Authentication.user.clientcache.reports.lastUIState = transition.$to().name;
        Authentication.syncClientCache();
      }
    }
  );
});

reportsModule.component('chart', chartComponent);
reportsModule.component('reportsTable', reportsTableComponent);
reportsModule.component('timeoffTable', timeoffTableComponent);
reportsModule.component('userTimeoffTable', userTimeoffTableComponent);
reportsModule.component('noShowTable', noShowTableComponent);
reportsModule.component('salesTable', salesTableComponent);
reportsModule.component('shiftFeedbackTable', shiftFeedbackTableComponent);
reportsModule.component('reportsCharts', reportsChartsComponent);
reportsModule.component('reportsSummary', reportsSummaryComponent);
reportsModule.component('configureTableColumns', configureTableColumnsComponent);
reportsModule.component('reportsSubNav', reportsSubNavComponent);
reportsModule.component('payrollTableGrouped', payrollTableGroupedComponent);
reportsModule.component('payrollTableUngrouped', payrollTableUngroupedComponent);
reportsModule.component('reportDynamicField', reportDynamicFieldComponent);
reportsModule.component('reportUserCell', reportUserCellComponent);

reportsModule.directive('reportTableColumn', reportTableColumn);
reportsModule.directive('salesTableColumn', salesTableColumn);
reportsModule.service('ReportsService', ReportsServiceImport);
reportsModule.service('ReportsUtils', ReportsUtilsImport);
reportsModule.factory('reportsPrintPayloadReplacer', ReportsPrintPayloadReplacerImport);

reportsModule.component('rBarChartComponent', r2a(BarChartComponentReact));
reportsModule.component('rLineGraphComponent', r2a(LineGraphComponentReact));

export default reportsModule.name;
